import { ReactNode } from "react";
import { Col, Row } from "reactstrap";
import { P } from "../../../../AbstractElements";
import EmployeeFieldGrid, {
  EmployeeFieldGridProps,
  EmployeeValueFormatter,
} from "../EmployeeFieldGrids";

export interface EmployeeDetailsPanelProps<T extends object> {
  /** Object or array of objects */
  entity: T | T[];

  /** Grid options forwarded to EmployeeFieldGrid */
  fieldOrder?: EmployeeFieldGridProps<T>["fieldOrder"];
  labelMap?: EmployeeFieldGridProps<T>["labelMap"];
  formatters?: Partial<Record<keyof T | string, EmployeeValueFormatter<T>>>;
  omitKeys?: EmployeeFieldGridProps<T>["omitKeys"];

  /** Wrapper class */
  className?: string;

  /** Array mode options */
  gridColumns?: 1 | 2 | 3 | 4; // default 2 -> md="6"
  getItemTitle?: (item: T, index: number) => ReactNode;
  getItemKey?: (item: T, index: number) => React.Key;
  itemClassName?: string;
  itemColMd?: 12 | 6 | 4 | 3; // overrides gridColumns
  fieldActions?: Partial<Record<keyof T | string, () => void>>;
  canEdit?: boolean;
  previewableKeys?: (keyof T | string)[]; // 👈 new
  onPreview?: (key: keyof T | string, value: unknown) => void; // 👈 new
}

const colMap: Record<number, 12 | 6 | 4 | 3> = { 1: 12, 2: 6, 3: 4, 4: 3 };

const EmployeeDetailsPanel = <T extends object>({
  entity,
  fieldOrder,
  labelMap,
  formatters,
  omitKeys,
  className,
  gridColumns = 2,
  getItemTitle,
  getItemKey = (item, i) => (item as any)?.id ?? i,
  itemClassName,
  itemColMd,
  fieldActions,
  canEdit,
  onPreview,
  previewableKeys,
}: EmployeeDetailsPanelProps<T>) => {
  const isArray = Array.isArray(entity);

  if (!isArray) {
    return (
      <div className={className}>
        <EmployeeFieldGrid<T>
          data={entity as T}
          fieldOrder={fieldOrder}
          labelMap={labelMap}
          formatters={formatters}
          omitKeys={omitKeys}
          fieldActions={fieldActions}
          canEdit={canEdit}
          onPreview={onPreview}
          previewableKeys={previewableKeys}
        />
      </div>
    );
  }

  const items = entity as T[];
  const mdSpan = itemColMd ?? colMap[gridColumns] ?? 6;

  return (
    <div className={className}>
      <Row className="g-3">
        {items.map((item, idx) => (
          <Col md={mdSpan} key={getItemKey(item, idx)}>
            <div className={itemClassName}>
              {getItemTitle ? (
                <P className="mb-2 f-14 fw-600">{getItemTitle(item, idx)}</P>
              ) : null}
              <EmployeeFieldGrid<T>
                data={item}
                fieldOrder={fieldOrder}
                labelMap={labelMap}
                formatters={formatters}
                omitKeys={omitKeys}
                fieldActions={fieldActions}
                canEdit={canEdit}
                onPreview={onPreview}
                previewableKeys={previewableKeys}
              />
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default EmployeeDetailsPanel;
